import type { LayoutItem } from "react-grid-layout"
import type { MetricMeta } from "./metric"
import type { PlayerSessionSummary } from "./report"
import type { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"

export type ChartType =
  | "activityInfo"
  | "combo"
  /** 트렌드 전용 — 기간 안의 액티비티를 X축에 둔다 (2026-10-02 요청) */
  | "activityGraph"
  | "point"
  | "sessionDetails"
  | "scatter"
  | "summary"
  | "bar"
  | "line"
  | "table"
  | "map"
  | "comparison"
  | "spacer"

export type BuilderChartType = "summary" | "bar" | "line" | "table" | "map" | "comparison"

export type MapSubType =
  | "formationMap"
  | "actionTrajectory"
  | "action18Zone"
  | "locationHeatMap"
  | "location18Zone"

export type ComparisonSubType = "pie" | "radar" | "treemap"

export interface BuilderPlayerData {
  idfPlayer: number
  playerName: string
  backNumber: number
  position: string
  summary: Record<string, number | null>
}

export type PaperSize = "a4" | "letter" | "legal"
export type PaperOrientation = "portrait" | "landscape"

export interface PaperConfig {
  size: PaperSize
  orientation: PaperOrientation
}

export const PAPER_DIMENSIONS: Record<PaperSize, { width: number; height: number }> = {
  a4: { width: 794, height: 1123 },
  letter: { width: 816, height: 1056 },
  legal: { width: 816, height: 1344 },
}

export type DataSourceType = "activity" | "session"

export interface SliceConfig {
  start: number
  count: number
}

export interface ChartDataConfig {
  metrics: string[]
  players: number[] | "all"
  calcType: "average" | "total"
  dataSource: DataSourceType
  sessions: number[] | "all"
  playerSlice?: SliceConfig
  metricSlice?: SliceConfig
  groupKey1?: string
  groupKey2?: string | null
}

export interface ChartDisplayOptions {
  showValues: boolean
  showLegend: boolean
  decimalPoints: number
  orientation?: "horizontal" | "vertical"
  colors?: string[]
  splitMode?: "off" | "auto" | "manual"
  maxRowsPerSection?: number
  maxColsPerSection?: number
  splitColumnLayout?: "vertical" | "horizontal"
}

export interface ActionTableConfig {
  actionType: string
}

export interface MapConfig {
  mapType: MapSubType
  actionType?: string
}

export interface ComparisonConfig {
  comparisonType: ComparisonSubType
}

export interface BuilderChartItem {
  id: string
  chartType: BuilderChartType
  title: string
  x: number
  y: number
  w: number
  h: number
  dataConfig: ChartDataConfig
  displayOptions: ChartDisplayOptions
  chartSpecificConfig?: ActionTableConfig | MapConfig | ComparisonConfig
}

export interface BuilderPaletteItem {
  type: BuilderChartType
  label: string
  description: string
  defaultWidth: number
  defaultHeight: number
  minWidth: number
  minHeight: number
}

export interface DataSourceConfig {
  type: "activity" | "session" | "player" | "trend"
  activityIds?: number[]
  sessionIds?: number[]
  playerIds?: number[]
  metrics?: string[]
  calcType?: "average" | "total"
  interval?: "1m" | "5m" | "15m"
  /** 리포트 템플릿 content 안에서 이 아이템이 따르는 섹션의 인덱스 (Table: Custom Table 섹션, Bar/Line: Graph 섹션). 없으면 첫 섹션 (#75) */
  sectionIndex?: number
  /** Graph 섹션 안의 몇 번째 차트를 따르는지 (settings 배열 인덱스). 없으면 0 */
  chartIndex?: number
}

export interface ChartOptions {
  title?: string
  showLegend?: boolean
  showGrid?: boolean
  colors?: string[]
  xAxisLabel?: string
  yAxisLabel?: string
  chartType?: "line" | "bar" | "area"
  stacked?: boolean
  xMetric?: string
  yMetric?: string
  columns?: string[]
  sortKey?: string
  sortOrder?: "asc" | "desc"
  showPlayerNames?: boolean
  showPlayerNumbers?: boolean
  content?: string
  fontSize?: number
  fontWeight?: "normal" | "bold"
  textAlign?: "left" | "center" | "right"
  // Bar/Line Chart options
  orientation?: "horizontal" | "vertical"
  decimalPlaces?: number // 1=0.1, 2=0.01, 3=0.001
  dataLabels?: "abovePlots" | "hideValues" | "separateTable"
  autoSize?: boolean // Auto-resize grid item based on player count & name lengths
  /** Summary 카드의 분류 이름 (지표 키 → 제목). 리포트 화면의 Volume·Intensity 같은 값 */
  summaryTitles?: Record<string, string>
  /** Summary 카드의 지표별 계산 방식(지표 키 → average/total). 없으면 템플릿 설정, 그것도 없으면 dataSource.calcType */
  summaryCalcTypes?: Record<string, "average" | "total">
  /** 혼합(Combo) 차트 — 지표별 종류(bar/stacked/point/line/area). 없으면 템플릿 축 종류, 그것도 없으면 bar (#75) */
  metricChartTypes?: Record<string, string>
  /** Chart by Player 지표별 최대값(수동 스케일). 아이템을 만들 때 템플릿 값을 스냅샷해 저장 뒤에도 유지한다 — null 은 자동 (2026-10-01 결정) */
  metricScales?: Record<string, number | null>
  /** Session Details 보기 — session(세션별) / 1m / 5m / 15m(인터벌). 팔레트에서 추가되는 네 아이템에 하나씩 */
  intervalView?: string
  /** Session Details 컨테이너가 보여 줄 보기 목록(session·1m·5m·15m). 있으면 컨테이너, 없으면 옛 단일 보기 아이템 */
  intervalViews?: string[]
  /** 같은 설정을 공유하는 아이템 묶음(Session Details 네 차트). 속성 패널 변경이 묶음 전체에 적용된다 */
  groupId?: string
  // Table options
  groupBy?: string
  groupBy2?: string | null
  splitTable?: "off" | "auto" | "manual"
  rowsPerPage?: number
  metricsPerPage?: number
  /** 분할된 표 덩어리 사이 공백(px). sectionGapMode 가 "manual" 일 때만 쓰인다 */
  sectionGap?: number
  /**
   * "auto"(기본): 덩어리 하나를 페이지 한 장 높이로 고정해 페이지 경계와 정확히 맞춘다.
   * "manual": sectionGap 값을 공백으로 쓴다.
   */
  sectionGapMode?: "auto" | "manual"
  // Map options
  mapType?: MapSubType
  actionType?: string
  mapColor?: string
  rotationSessionId?: number | null
  /** Action Map 오른쪽에 액션 표를 같이 그린다 (기본 true) */
  showActionTable?: boolean
  /** Action Map 에서 맵이 차지하는 폭 비율(0.2~0.8, 기본 0.4) — 맵과 표 사이 구분선을 끌어 바꾼다 */
  mapSplit?: number
  /**
   * 표 크기 방식(Table·Action Map 공통, 기본 auto).
   * auto: 아이템 하나에 길게 — 페이지 경계에서 행이 잘리지 않게 여백을 둔다.
   * manual: Table 은 장당 행·지표 수로 출력 장마다 아이템 하나, Action Map 은 세션 묶음마다 아이템 하나(맵은 첫 아이템에만)
   */
  tableScale?: "auto" | "manual"
  /** 배치 — auto: 페이지 경계에 걸리면 앞에 빈 공간을 두어 다음 페이지에서 시작, manual: 경계와 상관없이 그대로 그린다
   *  (2026-10-01 결정). Table·Action Map·Session Details 는 자기 분할 설정(tableScale)을 그대로 쓴다 */
  layoutMode?: "auto" | "manual"
  /** Chart by Player 방향 — 없으면 템플릿 Graph 설정(orientation), 그것도 없으면 가로 (#185) */
  chartOrientation?: "horizontal" | "vertical"
  /** 세로 차트의 장당 선수 수 — 기본 25, 세로 용지 최대 30·가로 20 (#185) */
  playersPerPage?: number
  /** 세로 차트의 X축 표시 — 없으면 템플릿(showAxis), 그것도 없으면 보임 (#185) */
  showXAxis?: boolean
  /** 값 표시 위치 — hideValues·insidePlots·abovePlots·separateTable. 없으면 템플릿 dataLabel (#185) */
  dataLabel?: string
  /** Table 의 요약 행 표시 — 템플릿에 요약 행이 있어도 아이템에서 각각 끌 수 있다. 기본 true (2026-09-30 요청) */
  showTotalRow?: boolean
  showAverageRow?: boolean
  /** Action Map 표에 보일 컬럼 키. 없으면 전부 (No. 열은 항상) */
  actionTableColumns?: string[]
  // Comparison options
  comparisonType?: ComparisonSubType
  showValues?: boolean
}

export interface CanvasItem {
  id: string
  chartType: ChartType
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
  maxW?: number
  maxH?: number
  dataSource: DataSourceConfig
  options: ChartOptions
  isLoading?: boolean
  error?: string | null
}

export type PageOrientation = "portrait" | "landscape"

export type PageSize = "a4" | "letter" | "custom"

export interface PageConfig {
  size: PageSize
  orientation: PageOrientation
  customWidth?: number
  customHeight?: number
  marginTop?: number
  marginBottom?: number
  marginLeft?: number
  marginRight?: number
}

export interface Page {
  id: string
  name?: string
  items: CanvasItem[]
  config: PageConfig
}

export interface PaletteItem {
  type: ChartType
  /** 같은 type 의 팔레트 항목이 여럿일 때(Session Details 의 세션/1분/5분/15분) 항목을 구분하는 고유 키. 없으면 type */
  key?: string
  label: string
  icon: string
  defaultWidth: number
  defaultHeight: number
  minWidth: number
  minHeight: number
  description?: string
  /** 항목을 추가할 때 아이템 옵션에 미리 넣는 값 (예: Session Details 의 intervalView) */
  presetOptions?: Partial<ChartOptions>
}

export interface PdfExportOptions {
  filename: string
  quality?: "low" | "medium" | "high"
  includePageNumbers?: boolean
  headerText?: string
  footerText?: string
}

/** 리포트 종류 하나의 작업 상태 — 배치와 Report Filters */
export interface ReportKindSnapshot {
  pages: Page[]
  currentPageId: string | null
  filterPeriod: { start: string; end: string }
  filterActivityId: number | null
  filterSessionIds: number[]
  filterPlayerIds: number[]
  filterReportMode: ACTIVITY_SESSION_TYPE
  filterReportTemplateId: number | null
}

export interface PdfBuilderState {
  pages: Page[]
  currentPageId: string | null
  selectedItemId: string | null
  isDraggingFromPalette: boolean
  draggedPaletteItem: PaletteItem | null
  isExporting: boolean
  /** 캔버스에서 아이템을 끌거나 크기를 바꾸는 중 — 자동 높이 계산이 손을 방해하지 않게 (2026-10-01). 저장하지 않는다 */
  isInteracting: boolean
  /** 페이지 경계에 걸린 아이템의 위 여백(px) — 제목까지 다음 페이지로 넘기려고 컴포넌트가 계산해 넣고 CanvasItem 이 적용한다. 저장하지 않는다 */
  headGaps: Record<string, number>
  /** 위 여백 때문에 칸 높이(h)에 더해 둔 격자 줄 수 — 내용 높이를 되찾을 때 뺀다. 저장하지 않는다 */
  headGapRows: Record<string, number>
  /** 여백을 더하기 전의 내용 줄 수 — 격자 되돌림(메아리)으로 h 가 흔들려도 내용 높이를 잃지 않게 따로 둔다. 저장하지 않는다 */
  headGapBase: Record<string, number>
  exportProgress: number
  filterActivityId: number | null
  filterSessionIds: number[]
  filterPlayerIds: number[]
  /** Report Filters — 액티비티/세션 모드. 리포트 화면의 dailyReportType 과 같은 의미 (#75) */
  filterReportMode: ACTIVITY_SESSION_TYPE
  /** Report Filters — 섹션 설정의 출처가 되는 리포트 템플릿. null 이면 최근 사용 템플릿 */
  filterReportTemplateId: number | null
  /** 어떤 리포트를 만드는지 — 액티비티(daily) / 트렌드(trend). 컴포넌트 구성·템플릿·필터가 달라진다 (2026-10-02 요청) */
  filterReportKind: "daily" | "trend"
  /** 기간 — 액티비티 목록·트렌드 집계 범위. "YYYY-MM-DD" (액티비티 기본 1년, 트렌드 기본 1개월) */
  filterPeriod: { start: string; end: string }
  /**
   * 리포트 종류를 오갈 때 되돌리려고 들고 있는 작업 상태 — 트렌드를 보러 갔다 와도 액티비티 쪽 배치가
   * 그대로 남아 있어야 한다 (2026-10-02 요청). 화면을 벗어나면 비운다. 저장하지 않는다
   */
  kindSnapshots: Partial<Record<"daily" | "trend", ReportKindSnapshot>>
}

export interface PdfBuilderActions {
  addPage: (config?: Partial<PageConfig>) => void
  removePage: (pageId: string) => void
  reorderPages: (fromIndex: number, toIndex: number) => void
  setCurrentPage: (pageId: string) => void
  updatePageConfig: (pageId: string, config: Partial<PageConfig>) => void
  addItem: (pageId: string, item: Omit<CanvasItem, "id">) => void
  removeItem: (pageId: string, itemId: string) => void
  updateItem: (pageId: string, itemId: string, updates: Partial<CanvasItem>) => void
  updateItemLayout: (pageId: string, itemId: string, layout: Partial<LayoutItem>) => void
  selectItem: (itemId: string | null) => void
  setHeadGap: (itemId: string, px: number, rows?: number, baseH?: number) => void
  duplicateItem: (pageId: string, itemId: string) => void
  setDraggingFromPalette: (isDragging: boolean, item?: PaletteItem) => void
  setExporting: (isExporting: boolean, progress?: number) => void
  setReportKind: (kind: "daily" | "trend") => void
  setPeriod: (period: { start: string; end: string }) => void
  setInteracting: (isInteracting: boolean) => void
  setFilterActivity: (id: number | null) => void
  setFilterSessions: (ids: number[]) => void
  setFilterPlayers: (ids: number[]) => void
  setFilterReportMode: (mode: ACTIVITY_SESSION_TYPE) => void
  setFilterReportTemplate: (id: number | null) => void
  /**
   * 편집 상태를 처음으로 되돌린다. keepFilters 면 Report Filters(리포트 종류·기간·액티비티·세션·선수·템플릿)는 그대로 둔다 —
   * 배치를 바꾸는 것일 뿐이므로 고른 범위를 다시 고르게 하지 않는다. 트렌드에서는 액티비티 리포트로 돌아가 버리기도 했다 (2026-10-02)
   */
  reset: (options?: { keepFilters?: boolean }) => void
}

export type PdfBuilderStore = PdfBuilderState & PdfBuilderActions

export type CanvasItemLayout = Pick<
  CanvasItem,
  "id" | "x" | "y" | "w" | "h" | "minW" | "minH" | "maxW" | "maxH"
> & {
  i: string
}

export type ItemsToLayouts = (items: CanvasItem[]) => LayoutItem[]

export type LayoutsToItemUpdates = (
  layouts: readonly LayoutItem[],
) => Array<{ id: string; updates: Partial<CanvasItem> }>

export type ReportSectionType =
  | "summary"
  | "graph"
  | "sessionDetails"
  | "scatterGraph"
  | "stats"
  | "tacticMap"
  | "performanceProfile"
  | "customTable"

export interface SectionDisplayOptions {
  chartType?: "line" | "bar"
  orientation?: "horizontal" | "vertical"
  splitConfig?: {
    enabled: boolean
    splitBy: "count" | "page"
    splitCount?: number
  }
  showLegend?: boolean
  showValues?: boolean
  decimalPoints?: number
}

export interface LoadedReportSection {
  id: string
  type: ReportSectionType
  title: string
  data: unknown
  isOnCanvas?: boolean
}

export interface SessionInfo {
  idfSession: number
  name: string
  type?: string
}

export interface ReportTemplate {
  id: string
  name: string
  description?: string
  createdAt: string
  updatedAt: string
  paperConfig: {
    size: PaperSize
    orientation: PaperOrientation
  }
  pages: TemplatePageConfig[]
}

export interface TemplatePageConfig {
  id: string
  items: TemplateChartItem[]
}

export type TemplateChartItem = Omit<BuilderChartItem, "id"> & { id: string }
